import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, ReplaySubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpService } from '@supoui/core/services/http.service';
import { NGXLogger } from 'ngx-logger';
import { Dictionary, NumberDictionary } from '@supoui/shared/model/dictionary';
import { IStakeholderType } from '@supoui/shared/model/stakeholder-type';
import { IServiceItem } from '@supoui/shared/model/service-item';
import { IServiceType } from '@supoui/shared/model/service-type';
import { IService } from '@supoui/shared/model/service';
import { CountryAdapter } from '@supoui/shared/model/country';
import { IReduction } from '@supoui/shared/model/reduction';

@Injectable({
  providedIn: 'root'
})
export class DictionaryService {
  private readonly supplierPortalApp = 'SUPPLIER_PORTAL';

  private cachedStakeHolderTypes = new ReplaySubject<IStakeholderType[]>(1);
  private cachedStakeHolderTypesSize = 0;
  private cachedStakeHolderTypesLoading = false;

  private cachedServiceItems = new ReplaySubject<IServiceItem[]>(1);
  private cachedServiceItemsSize = 0;
  private cachedServiceItemsLoading = false;

  private cachedServiceTypes = new ReplaySubject<IServiceType[]>(1);
  private cachedServiceTypesSize = 0;
  private cachedServiceTypesLoading = false;

  private cachedServices = new ReplaySubject<IService[]>(1);
  private cachedServicesSize = 0;
  private cachedServicesLoading = false;

  private cachedSupportedBaseCurrencies = new ReplaySubject<string[]>(1);
  private cachedSupportedBaseCurrenciesSize = 0;
  private cachedSupportedBaseCurrenciesLoading = false;

  private cachedReductions = new ReplaySubject<IReduction[]>(1);
  private cachedReductionsSize = 0;
  private cachedReductionsLoading = false;

  private cachedAllReductions = new ReplaySubject<IReduction[]>(1);
  private cachedAllReductionsSize = 0;
  private cachedAllReductionsLoading = false;

  constructor(private httpService: HttpService, private countryAdapter: CountryAdapter, private logger: NGXLogger) {}

  public getProductGeoCoverageAreas(productId: number): Observable<NumberDictionary[]> {
    return this.httpService.get<Dictionary[]>(`services/onepoint/api/products/${productId}/countryLists`).pipe(
      map((data: any[]) => data.map(item => new NumberDictionary(item.id, item.name))),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getInsuranceLookupMethodList(): Observable<Dictionary[]> {
    return this.httpService.get<Dictionary[]>(`services/casemanagement/api/insurances/insurance-lookup-method-list`);
  }

  public getStakeholdersTypes(): Observable<IStakeholderType[]> {
    if (this.cachedStakeHolderTypesSize === 0 && !this.cachedStakeHolderTypesLoading) {
      this.cachedStakeHolderTypesLoading = true;
      this.fetchStakeholderTypes().subscribe(
        result => {
          this.cachedStakeHolderTypes.next(result);
          this.cachedStakeHolderTypesLoading = false;
          return this.cachedStakeHolderTypes.asObservable();
        },
        error => {
          this.cachedStakeHolderTypesLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedStakeHolderTypes.asObservable();
  }

  private fetchStakeholderTypes(): Observable<IStakeholderType[]> {
    return this.httpService.get<IStakeholderType[]>('services/casemanagement/api/stakeholders/stakeholdersTypes');
  }

  public getServiceItems(): Observable<IServiceItem[]> {
    if (this.cachedServiceItemsSize === 0 && !this.cachedServiceItemsLoading) {
      this.cachedServiceItemsLoading = true;
      this.fetchServiceItems().subscribe(
        result => {
          this.cachedServiceItems.next(result);
          this.cachedServiceItemsSize = result?.length;
          this.cachedServiceItemsLoading = false;
          return this.cachedServiceItems.asObservable();
        },
        error => {
          this.cachedServiceItemsLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedServiceItems.asObservable();
  }

  private fetchServiceItems(): Observable<IServiceItem[]> {
    return this.httpService.get<IServiceItem[]>('services/onepoint/api/masterlist/service-item-list/application/' + this.supplierPortalApp);
  }

  public getServiceTypes(): Observable<IServiceType[]> {
    if (this.cachedServiceTypesSize === 0 && !this.cachedServiceTypesLoading) {
      this.cachedServiceTypesLoading = true;
      this.fetchServiceTypes().subscribe(
        result => {
          this.cachedServiceTypes.next(result);
          this.cachedServiceTypesLoading = false;
          return this.cachedServiceTypes.asObservable();
        },
        error => {
          this.cachedServiceTypesLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedServiceTypes.asObservable();
  }

  private fetchServiceTypes(): Observable<IServiceType[]> {
    return this.httpService.get<IServiceType[]>('services/onepoint/api/masterlist/service-type-list/application/' + this.supplierPortalApp);
  }

  public getServices(): Observable<IService[]> {
    if (this.cachedServicesSize === 0 && !this.cachedServicesLoading) {
      this.cachedServicesLoading = true;
      this.fetchServices().subscribe(
        result => {
          this.cachedServices.next(result);
          this.cachedServicesLoading = false;
          return this.cachedServices.asObservable();
        },
        error => {
          this.cachedServicesLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedServices.asObservable();
  }

  private fetchServices(): Observable<IService[]> {
    return this.httpService.get<IService[]>('services/onepoint/api/sos-services');
  }

  getSupportedBaseCurrencies(): Observable<string[]> {
    if (this.cachedSupportedBaseCurrenciesSize === 0 && !this.cachedSupportedBaseCurrenciesLoading) {
      this.cachedSupportedBaseCurrenciesLoading = true;
      this.fetchSupportedBaseCurrencies().subscribe(
        result => {
          this.cachedSupportedBaseCurrencies.next(result);
          this.cachedSupportedBaseCurrenciesLoading = false;
          return this.cachedSupportedBaseCurrencies.asObservable();
        },
        error => {
          this.cachedSupportedBaseCurrenciesLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedSupportedBaseCurrencies.asObservable();
  }

  private fetchSupportedBaseCurrencies(): Observable<string[]> {
    return this.httpService.get<string[]>(`services/financebridge/api/exchangerates/supportedBaseCurrencies`);
  }

  public getReductions(): Observable<IReduction[]> {
    if (this.cachedReductionsSize === 0 && !this.cachedReductionsLoading) {
      this.cachedReductionsLoading = true;
      this.fetchReductions(true).subscribe(
        result => {
          this.cachedReductions.next(result);
          this.cachedReductionsLoading = false;
          return this.cachedReductions.asObservable();
        },
        error => {
          this.cachedReductionsLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedReductions.asObservable();
  }

  public getAllReductions(): Observable<IReduction[]> {
    if (this.cachedAllReductionsSize === 0 && !this.cachedAllReductionsLoading) {
      this.cachedAllReductionsLoading = true;
      this.fetchReductions(false).subscribe(
        result => {
          this.cachedAllReductions.next(result);
          this.cachedAllReductionsLoading = false;
          return this.cachedAllReductions.asObservable();
        },
        error => {
          this.cachedAllReductionsLoading = false;
          this.logger.error(error);
        }
      );
    }
    return this.cachedAllReductions.asObservable();
  }

  private fetchReductions(applicationFiltered: boolean): Observable<IReduction[]> {
    if (applicationFiltered) {
      return this.httpService.get<IReduction[]>('services/onepoint/api/masterlist/reductions/application/' + this.supplierPortalApp);
    } else {
      return this.httpService.get<IReduction[]>('services/onepoint/api/masterlist/reductions');
    }
  }
}
